import React, { useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EyeIcon } from "lucide-react";

import { PageHeader } from "@shared/components/PageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { RunHistoryFilter } from "./filter";
import { useGetMeterRunHistory } from "@features/mx/readCycleManagement/hooks";

interface RunData {
  id: string;
  runId: string;
  triggeredBy: string;
  runStartDate: string;
  runEndDate: string;
  status: string;
  totalRoutes: number;
  totalMeters: number;
  metersRead: number;
  completionPercentage: string;
  exceptions: number;
}

interface RunHistoryFilters {
  status: string;
}

export default function RunHistoryTab() {
  const { cycle_id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read all params from URL including page
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const statusFilter = searchParams.get("status") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: meterRunHistory } = useGetMeterRunHistory(
    cycle_id,
    currentPage,
    searchQuery,
    statusFilter
  );

  // Status options for filter
  const statusOptions = useMemo(
    () => [
      { value: "not_dispatched", label: "Not Dispatched" },
      { value: "dispatched", label: "Dispatched" },
      { value: "pending", label: "Pending" },
      { value: "completed", label: "Completed" }
    ],
    []
  );

  // Calculate pagination based on filtered data
  const pagination = useMemo(() => {
    const totalItems = meterRunHistory?.count || 0;
    const totalPages = Math.ceil(totalItems / 10);
    const startItem = (currentPage - 1) * 10 + 1;
    const endItem = Math.min(currentPage * 10, totalItems);

    return {
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: 10,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    };
  }, [meterRunHistory?.count, currentPage]);

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(statusFilter && { status: statusFilter }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Apply filters - now accepts filters parameter like PremisesTab
  const handleApplyFilters = (filters: RunHistoryFilters) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(filters.status && { status: filters.status }),
    };

    // Remove status param if empty or "All Status"
    if (!filters.status || filters.status === "All Status") {
      delete newParams.status;
    }

    // Reset to page 1 on filter change
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Reset filters
  const handleResetFilters = () => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.status;
    delete newParams.search;
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Handle view run
  const handleViewRun = (run: RunData) => {
    navigate(`/mx/reads/detail/manual/${run?.id}?tab=reads`);
  };

  // Define table columns
  const columns = useMemo<TableColumn<RunData>[]>(
    () => [
      {
        key: "startDate",
        header: "Run Start Date",
        sortable: true,
      },
      {
        key: "endDate",
        header: "Run End Date",
        sortable: true,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        render: (value) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${value === "Completed"
              ? "bg-green-100 text-green-800"
              : value === "Running"
                ? "bg-blue-100 text-blue-800"
                : value === "Scheduled"
                  ? "bg-gray-100 text-gray-800"
                  : "bg-red-100 text-red-800"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        key: "meterSchedule",
        header: "Total Routes",
        sortable: true,
        render: (row) => <span>{row?.meterCycle?.routeCount}</span>,
      },
      {
        key: "summary",
        header: "Total Resources",
        sortable: true,
        render: (value) => <span>{value?.meterCount}</span>,
      },
      {
        key: "summary",
        header: "Reads",
        sortable: true,
        render: (value) => <span>{value?.readingCount}</span>,
      },
      {
        key: "completionPercentage",
        header: "Completion %",
        sortable: true,
      },
      {
        key: "summary",
        header: "Exemptions",
        sortable: true,
        render: (value) => <span>{value?.exemptedCount}</span>,
      },
    ],
    []
  );

  // Actions for the table
  const actions: TableAction[] = [
    {
      label: "View",
      icon: <EyeIcon className="text-gray-500" />,
      onClick: (row) => {
        handleViewRun(row);
      },
    },
  ];

  const filterPanel = (
    <RunHistoryFilter
      status={statusFilter}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
      statusOptions={statusOptions}
    />
  );

  const hasActiveFilters = statusFilter && statusFilter !== "All Status";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Runs History"
        subtitle="Monitor and filter execution history for this read cycle"
      />

      <div className="space-y-4">
        <GenericTable
          data={meterRunHistory?.results || []}
          columns={columns}
          rowKey="id"
          actions={actions}
          actionsType="icons"
          emptyMessage="No runs found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          filterPanel={filterPanel}
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  1 filter(s) active
                </span>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}